import { CategoricalPalette, SdBin, WhpClass } from './types';

export type ResiliencePriorityTier = 'tier_1' | 'tier_2' | 'tier_3' | 'tier_4' | 'tier_5';

// 5-tier priority spectrum colors. Hex values MUST stay byte-identical to
// PRIORITY_TIER_COLORS in scripts/generate-resilience-priority-overlay.py.
// Reuses the WHP RdYlGn ramp so the spectrum reads as a fire-risk gradient
// on private acres, visually consistent with the WHP layer + matrix card.
export const RESILIENCE_PRIORITY_PALETTE: CategoricalPalette<ResiliencePriorityTier> = {
  colors: {
    tier_1: '#1A9850',  // watch / lowest
    tier_2: '#91CF60',  // low
    tier_3: '#FEE08B',  // moderate
    tier_4: '#FC8D59',  // high
    tier_5: '#D73027',  // top priority
  },
  labels: {
    tier_1: 'Watch',
    tier_2: 'Low',
    tier_3: 'Moderate',
    tier_4: 'High',
    tier_5: 'Top priority',
  },
  // Render top → bottom in the legend (top priority listed first).
  order: ['tier_5', 'tier_4', 'tier_3', 'tier_2', 'tier_1'],
};

// Resilience Priority decision matrix — single source of truth for the
// (WHP class × Relative-Density bin) → priority tier mapping. Used by both
// the precompute script (mirrored as a numpy lookup) and the matrix insight
// card (cell coloring + top-tier acre rollup).
//
// Rationale per cell:
//   tier_5 (Top priority)  — high or very-high WHP intersected with stands
//                            that are dense or overstocked. Both fuels and
//                            structure indicate the highest treatment urgency.
//   tier_4 (High)          — high fire risk on moderately stocked stands, OR
//                            very-high fire risk on moderately stocked stands.
//                            Treatment is meaningful but less urgent than
//                            tier_5 because either fuel load or structural
//                            density is one bin lower.
//   tier_3 (Moderate)      — moderate fire risk on overstocked stands
//                            (productivity-driven thinning), OR very-high WHP
//                            on already-open stands (active fuels mgmt).
//   tier_2 (Low)           — low-or-moderate WHP × moderate-to-overstocked
//                            density. Worth tracking, low intervention urgency.
//   tier_1 (Watch)         — low fire risk × open stands. Routine monitoring.
//
// IMPORTANT: when this matrix is updated, mirror the change in
// scripts/generate-resilience-priority-overlay.py and re-run the precompute
// + S3 sync, otherwise the on-screen overlay PNG and the matrix card will
// disagree.
export const RESILIENCE_PRIORITY_MATRIX: Record<WhpClass, Record<SdBin, ResiliencePriorityTier>> = {
  very_low:  { '0_to_30': 'tier_1', '30_to_60': 'tier_1', '60_to_80': 'tier_2', '80_plus': 'tier_2' },
  low:       { '0_to_30': 'tier_1', '30_to_60': 'tier_2', '60_to_80': 'tier_2', '80_plus': 'tier_3' },
  moderate:  { '0_to_30': 'tier_2', '30_to_60': 'tier_2', '60_to_80': 'tier_3', '80_plus': 'tier_4' },
  high:      { '0_to_30': 'tier_2', '30_to_60': 'tier_3', '60_to_80': 'tier_4', '80_plus': 'tier_5' },
  very_high: { '0_to_30': 'tier_3', '30_to_60': 'tier_4', '60_to_80': 'tier_5', '80_plus': 'tier_5' },
};

export const resiliencePriorityTier = (whp: WhpClass, rd: SdBin): ResiliencePriorityTier =>
  RESILIENCE_PRIORITY_MATRIX[whp][rd];
